import React, { useState, useEffect } from "react";
import { Paper, Typography, Grid2, Divider } from "@mui/material";
import { useForm, useFieldArray } from 'react-hook-form';
import axios from "axios";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from 'zod';
import { IconTrash } from "@tabler/icons-react";

import BreadCrumbs from "@components/breadCrumbs/breadCrumbs";
import CustomButton from "@components/button/customButton";
import ConfirmDialog from "@components/dialog/confirmDialog";
import SkeletonWrapper from "@components/skeleton/skeletonWrapper";
import ControlledTextField from "@components/inputs/controlledTextField";
import ControlledAutocomplete from "@components/inputs/controlledAutocomplete";
import CustomIconButton from "@components/button/customIconButton";

import { useSnackbar } from "@context/snackbarContext";
import { useAuthentication } from "@redux/authentication/hook";

import { AdminApiEndpoints } from "@configuration/apiEndpoints";

import { QUESTION_TYPE } from "@utils/constant";
import { formatDateTime } from "@utils/formatter";

const schema = z.object({
  title: z.string().min(1, 'Title is required'),
  questionEng: z.string().min(1, { message: 'Question is required' }),
  questionCnsimp: z.string().min(1, { message: '问题 is required' }),
  questionCntrad: z.string().min(1, { message: '問題 is required' }),
  // questionType: z.string({invalid_type_error: 'Question Type is required'}).min(1, { message: 'Question Type is required' }),
  questionType: z.object({
      questionType: z.string().min(1, "Label is required"),
      questionTypeGuid: z.string().min(1, "Value is required"),
    }, {invalid_type_error: 'Question Type is required'}),
  commonType: z.string({invalid_type_error: 'Common Type is required'}).min(1, { message: 'Common Type is required' }),
  maxAllowed: z.union([
    z.string().min(1, "Max Allowed is required").regex(/^\d+(\.\d+)?$/, "Max Allowed must be a valid number"),
    z.number().positive("Max Allowed must be a positive number"),
  ]),

  questionOption: z
    .array(
      z.object({
        optionEng: z.string().min(1, 'Option Text is required'),
        optionCnsimp: z.string().min(1, '选项 is required'),
        optionCntrad: z.string().min(1, '選項 is required'),
        subText: z.string({invalid_type_error: 'Sub Textbox is required'}).min(1, { message: 'Sub Textbox is required' }),
      })
    ).optional(),

  /* view only */
  questionNumber: z.string().optional(),
  totalChoices: z.string().optional(),
  createdOn: z.string().optional(),
  createdBy: z.string().optional(),
}).refine(
  (data) =>  ![QUESTION_TYPE.MULTI_OPTION_MULTI_CHOICE, QUESTION_TYPE.MULTI_OPTION_SINGLE_CHOICE].includes(data?.questionType.questionType) || (data.questionOption && data.questionOption.length >= 2),
  {
    message: 'Choices are required and must have at least 2 choices',
    path: ['choices'],
  }
).refine(
  (data) => {
    if (data.questionType.questionType === QUESTION_TYPE.MULTI_OPTION_MULTI_CHOICE) {
      const maxAllowed = typeof data.maxAllowed === 'string' ? parseFloat(data.maxAllowed) : data.maxAllowed;
      return maxAllowed >= 2 && maxAllowed <= (data.questionOption?.length || 0);
    }
    return true;
  },
  {
    message: 'Max Allowed must be at least 2 and cannot exceed the number of choices',
    path: ['maxAllowed'],
  }
);

const questionTypeOptions = [
  {
    questionType: 'Single Answer',
    questionTypeGuid: 'ee14ae53-bfd1-4f45-a618-ac46f6ebf322',
  },
  {
    questionType: 'Multi-Option & Single Choice',
    questionTypeGuid: '20a564e3-f45a-41a1-925d-66787d86dec0',
  },
  {
    questionType: 'Multi-Option & Multi-Choice',
    questionTypeGuid: '46b2f0ad-7ee0-47b5-aaa2-7287f9d2b465',
  },
]

export default function QuestionForm() {
  const queryClient = useQueryClient()
  const openSnackbar = useSnackbar();
  const navigate = useNavigate()
  const { pathname } = useLocation();
  const { user } = useAuthentication()
  const pageAction = pathname.split("/")[3] || null; // get from pathname expected values ["create" || "view" || "edit"]
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");

  const { handleSubmit, control, getValues, reset, watch, setValue } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      questionOption: [],
    },
  });

  // console.log(errors)

  const { fields, append, remove } = useFieldArray({
    control,
    name: "questionOption",
  });

  const [openConfirmDialog, setOpenConfirmDialog] = useState(false)

  useEffect(()=>{
    if (((pageAction === "edit" || pageAction === "view") && !id) || (pageAction === "create" && id)) {
      onBack()
    }
    if (id) {
      fetchQuestionMutation.mutate(id)
    }
  },[pageAction])

  const fetchQuestionMutation = useMutation({
    mutationFn: (id) => {
      return axios.get(`${AdminApiEndpoints.getQuestion}/${id}`)
    },
    onSuccess: (res) => {
      const {createdOn, questionType, questionTypeGuid, questionOptions, ...data} = res.data
      // console.log(data)
      const questionTypeValue = questionType.valueEng
      const questionOption = questionOptions.map(item => ({
        ...item,
        subText: item.subText === true ? "Yes" : "No"
      }));
      reset({
        ...data,
        createdOn: formatDateTime(createdOn),
        questionType: {questionType: questionTypeValue, questionTypeGuid },
        questionOption
      })
    },
    onError: (err) => {
      openSnackbar("Failed to retrieve question", "error");
    }
  });

  const questionTypeOptions = useQuery({
    queryKey: ['getQuestionTypeList'],
    queryFn: () =>
      axios.get(AdminApiEndpoints.getQuestionTypeList),
    initialData: [],
    select: (res) => {
      const data = res?.data || [];
      return data.map(item => ({
          questionTypeGuid: item.parameterGuid,
          questionType: item.valueEng
      }));
  },
  })

  const createQuestionMutation = useMutation({
    mutationFn: (data) => {
      return axios.post(AdminApiEndpoints.createQuestion, {...data, by: user?.username})
    },
    onSuccess: () => {
      setOpenConfirmDialog(false)
      queryClient.invalidateQueries({ queryKey: ['getQuestion'] })
      openSnackbar("Successfully created question", "success");
      onBack()
    },
    onError: (err) => {
      setOpenConfirmDialog(false)
      if (err?.response) openSnackbar(err?.response?.data, "error");
      else openSnackbar("Failed to create question", "error");
    }
  })

  const updateQuestionMutation = useMutation({
    mutationFn: (data) => {
      return axios.put(`${AdminApiEndpoints.updateQuestion}/${id}`, {...data, by: user?.username})
    },
    onSuccess: () => {
      setOpenConfirmDialog(false)
      queryClient.invalidateQueries({ queryKey: ['getQuestion'] })
      openSnackbar("Successfully updated question", "success");
      fetchQuestionMutation.mutate(id)
    },
    onError: (err) => {
      setOpenConfirmDialog(false)
      if (err?.response) openSnackbar(err?.response?.data, "error");
      else openSnackbar("Failed to update question", "error");
    }
  })

  const onSubmit = () => {
    setOpenConfirmDialog(true)
  };

  const onConfirmSubmit = () => {
    const data = getValues();
    data.questionTypeGuid = data?.questionType?.questionTypeGuid
    if(data.questionOption.length > 0){
      data.questionOption = data.questionOption.map(item => ({
        ...item,
        subText: item.subText === "Yes" ? true : false
      }));
    }
    if(data?.questionType?.questionType !== QUESTION_TYPE.MULTI_OPTION_MULTI_CHOICE){
      data.maxAllowed = 1
    }
    if(pageAction === "create")
      createQuestionMutation.mutate(data)
    else
      updateQuestionMutation.mutate(data)
  }

  const onBack = () => {
    navigate('/admin/question-setup', { replace: true });
  }

  const watchQuestionType = watch('questionType')

  useEffect(()=>{
    if(pageAction === "create"){
      if(watchQuestionType?.questionType === QUESTION_TYPE.SINGLE_ANSWER){
        setValue('maxAllowed', '1')
        setValue('questionOption', [])
      }
      else if(watchQuestionType?.questionType === QUESTION_TYPE.MULTI_OPTION_SINGLE_CHOICE){
        setValue('maxAllowed', '1')
        setValue('questionOption', [{ optionCntrad: "", optionCnsimp: "", optionEng: "", subText: "No" }, { optionCntrad: "", optionCnsimp: "", optionEng: "", subText: "No" }])
      }
      else if(watchQuestionType?.questionType === QUESTION_TYPE.MULTI_OPTION_MULTI_CHOICE){
        setValue('maxAllowed', '2')
        setValue('questionOption', [{ optionCntrad: "", optionCnsimp: "", optionEng: "", subText: "No" }, { optionCntrad: "", optionCnsimp: "", optionEng: "", subText: "No" }])
      }
    }
    else{
      if(watchQuestionType?.questionType === QUESTION_TYPE.MULTI_OPTION_MULTI_CHOICE){
        // Change all subText: "No" if "Multi-Option & Multi-Choice"
        const updatedSubTextbox = fields.map((data)=>({...data, subText: "No"}))
        setValue('questionOption', updatedSubTextbox)
      }
    }
  },[watchQuestionType, pageAction])

  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: "25px"}}>
      <BreadCrumbs />
      <SkeletonWrapper isLoading={fetchQuestionMutation.isPending} isError={fetchQuestionMutation.isError} errorMessage="Unable to load question" disabled={["create"].includes(pageAction)}>
      <form onSubmit={handleSubmit(onSubmit)} style={{display: 'flex', flexDirection: 'column', gap: "25px"}}>
        <Paper
          sx={{
            p: "20px 25px",
            boxShadow: "none",
            borderRadius: "10px",
            border: "1px solid #E5E5E5",
          }}
        >
          <Grid2 container spacing={"20px"}>
            <Grid2 size={12}>
              <Typography fontSize={"0.9rem"} color="#202020" fontWeight={600}>Question Detail</Typography>
            </Grid2>
            <Grid2 size={12}>
              <ControlledTextField 
                name="title"
                control={control}
                label="Title"
                disabled={fetchQuestionMutation.isPending || fetchQuestionMutation.isError}
                readOnly={["view"].includes(pageAction)}
              />
            </Grid2>
            
            <Grid2 size={12}>
              <ControlledTextField 
                name="questionCnsimp"
                control={control}
                label="问题 (简体)"
                disabled={fetchQuestionMutation.isPending || fetchQuestionMutation.isError}
                readOnly={["view"].includes(pageAction)}
                multiline
                rows={2}
              />
            </Grid2>

            <Grid2 size={12}>
              <ControlledTextField 
                name="questionCntrad"
                control={control}
                label="問題 (繁体)"
                disabled={fetchQuestionMutation.isPending || fetchQuestionMutation.isError}
                readOnly={["view"].includes(pageAction)}
                multiline
                rows={2}
              />
            </Grid2>

            <Grid2 size={12}>
              <ControlledTextField 
                name="questionEng"
                control={control}
                label="Question"
                disabled={fetchQuestionMutation.isPending || fetchQuestionMutation.isError}
                readOnly={["view"].includes(pageAction)}
                multiline
                rows={2}
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledAutocomplete 
                name="questionType"
                control={control}
                label="Question Type"
                disabled={fetchQuestionMutation.isPending || fetchQuestionMutation.isError || ["view"].includes(pageAction)}
                loading={questionTypeOptions.isFetching}
                options={questionTypeOptions.data}
                getOptionLabel={(option) => option.questionType}
                getOptionKey={(option) => option.questionTypeGuid}
                isOptionEqualToValue={(option, value) => option.questionTypeGuid === value?.questionTypeGuid}
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledAutocomplete 
                name="commonType"
                control={control}
                label="Common Type"
                disabled={fetchQuestionMutation.isPending || fetchQuestionMutation.isError || ["view"].includes(pageAction)}
                options={["Common", "Normal", "Unique"]}
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledTextField 
                name="maxAllowed"
                control={control}
                label="Max Allowed"
                disabled={fetchQuestionMutation.isPending || fetchQuestionMutation.isError}
                readOnly={["view"].includes(pageAction) ||  [QUESTION_TYPE.SINGLE_ANSWER, QUESTION_TYPE.MULTI_OPTION_SINGLE_CHOICE].includes(watchQuestionType?.questionType)}
                type="number"
              />
            </Grid2>

            {["view", "edit"].includes(pageAction) && <>
            
            <Grid2 size={{ xs: 12 }}>
              <Divider />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledTextField 
                name="questionNumber"
                control={control}
                label="Question No"
                disabled={true}
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledTextField 
                name="totalChoices"
                control={control}
                label="Total Choices"
                disabled={true}
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledTextField 
                name="createdOn"
                control={control}
                label="Created On"
                disabled={true}
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledTextField 
                name="createdBy"
                control={control}
                label="Created By"
                disabled={true}
              />
            </Grid2>
            
            </>}

          </Grid2>
        </Paper>
        {[QUESTION_TYPE.MULTI_OPTION_MULTI_CHOICE, QUESTION_TYPE.MULTI_OPTION_SINGLE_CHOICE].includes(watchQuestionType?.questionType) &&
        <Paper
          sx={{
            p: "20px 25px",
            boxShadow: "none",
            borderRadius: "10px",
            border: "1px solid #E5E5E5",
          }}
        >
          <Grid2 container spacing={"20px"}>
            <Grid2 size={12}>
              <Typography fontSize={"0.9rem"} color="#202020" fontWeight={600}>Question Choice(s)</Typography>
            </Grid2>
            {fields.map((item, index) => (
            <React.Fragment key={item.id}>

              <Grid2 size={{ lg: "grow", md: "grow", sm: 12, xs: 12 }}>
                <ControlledTextField 
                  name={`questionOption.${index}.optionCnsimp`}
                  control={control}
                  label="选项 (简体)"
                  disabled={fetchQuestionMutation.isPending || fetchQuestionMutation.isError}
                  readOnly={["view"].includes(pageAction)}
                />
              </Grid2>

              <Grid2 size={{ lg: "grow", md: "grow", sm: 12, xs: 12 }}>
                <ControlledTextField 
                  name={`questionOption.${index}.optionCntrad`}
                  control={control}
                  label="選項 (繁体)"
                  disabled={fetchQuestionMutation.isPending || fetchQuestionMutation.isError}
                  readOnly={["view"].includes(pageAction)}
                />
              </Grid2>

              <Grid2 size={{ lg: "grow", md: "grow", sm: 12, xs: 12 }}>
                <ControlledTextField 
                  name={`questionOption.${index}.optionEng`}
                  control={control}
                  label="Option Text"
                  disabled={fetchQuestionMutation.isPending || fetchQuestionMutation.isError}
                  readOnly={["view"].includes(pageAction)}
                />
              </Grid2>

              <Grid2 size={{ xl: 1, lg: 1.5, md: 2, sm: 12, xs: 12 }}>
                <ControlledAutocomplete 
                  name={`questionOption.${index}.subText`}
                  control={control}
                  label="Sub Textbox"
                  disabled={fetchQuestionMutation.isPending || fetchQuestionMutation.isError || ["view"].includes(pageAction) || [QUESTION_TYPE.MULTI_OPTION_MULTI_CHOICE, QUESTION_TYPE.SINGLE_ANSWER].includes(watchQuestionType?.questionType)}
                  options={["No", "Yes"]}
                />
              </Grid2>

              {(["create", "edit"].includes(pageAction) && fields?.length > 2) &&
                <Grid2 size={{ lg: "auto", md: "auto", sm: 12, xs: 12 }} display={"flex"} justifyContent={"flex-end"} alignItems={"flex-start"}>
                  <CustomIconButton aria-label="Remove" color="#D13F58" onClick={() => remove(index)}>
                    <IconTrash />
                  </CustomIconButton>
                </Grid2>
              }

              <Grid2 size={{ xs: 12 }}>
                <Divider />
              </Grid2>

              {(["create", "edit"].includes(pageAction) && index === fields?.length - 1) && 
              <Grid2 size={{ xs: 12 }} display={"flex"} justifyContent={"flex-end"} gap={"10px"}>
                <CustomButton onClick={() => append({optionCntrad: "", optionCnsimp: "", optionEng: "", subText: "No" })}>
                  Add Choice
                </CustomButton>
              </Grid2>
              }
            </React.Fragment>
          ))}
          </Grid2>
        </Paper>
        }
        {["create", "edit"].includes(pageAction) && 
          <Paper
            sx={{
              p: "20px 25px",
              boxShadow: "none",
              borderRadius: "10px",
              border: "1px solid #E5E5E5",
              overflowX: "auto",
              display: "flex",
              justifyContent: 'flex-end',
              gap: "10px"
            }}
          >
            <CustomButton type="submit" disabled={createQuestionMutation.isPending || fetchQuestionMutation.isError}>Submit</CustomButton>
          </Paper>
        }
      </form>
      </SkeletonWrapper>
      <ConfirmDialog
        open={openConfirmDialog}
        onClose={()=>setOpenConfirmDialog(false)}
        dialogTitle="Confirmation"
        dialogDescription="Submit question?"
        onSubmit={()=>onConfirmSubmit()}
        loading={createQuestionMutation.isPending || updateQuestionMutation.isPending}
      />
    </div>
  );
}
